/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { Button, Card, Table } from "@jhool-io/fe-components";
import { ColumnDef } from "@tanstack/react-table";
import {
    IPaymentLinkDetails,
    IPaymentLinkInvoice,
    PaymentLinkBillType,
} from "../../types/payment-link.types";
import {
    formatDate,
    makeStringFirstLetterCapital,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import CreditCardIcon from "../../../../components/Icons/CreditCard";
import axiosInstance from "../../../../utils/axios";

const PAYMENT_LINK_BILL_TYPE_LABELS: Record<PaymentLinkBillType, string> = {
    ci: "Session fee",
    pr100: "PR100",
};

interface PaymentLinkDetailsProps {
    paymentLinkData: IPaymentLinkDetails;
    onContinueToPayment: () => void;
}

export default function PaymentLinkDetails({
    paymentLinkData,
    onContinueToPayment,
}: PaymentLinkDetailsProps) {
    const handleContinue = () => {
        onContinueToPayment();
        if (!axiosInstance.defaults.headers.common.practice_id) {
            axiosInstance.defaults.headers.common.practice_id =
                paymentLinkData.practice_id;
        }
    };

    const calculateTotal = React.useMemo(() => {
        return paymentLinkData.invoices.reduce(
            (sum, invoice) => sum + invoice.amount,
            0
        );
    }, [paymentLinkData.invoices]);

    const parseDateForFormat = (dateString: string): string => {
        const mmddyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
        const match = dateString.match(mmddyyyyPattern);
        if (match) {
            const [, month, day, year] = match;
            return `${year}-${month}-${day}`;
        }
        return dateString;
    };

    const columns: ColumnDef<IPaymentLinkInvoice>[] = React.useMemo(
        () => [
            {
                accessorKey: "client_name",
                header: "Client Name",
                cell: () => paymentLinkData.client_name || "--",
            },
            {
                accessorKey: "provider_name",
                header: "Provider Name",
                cell: ({ row }) => row.original.provider_name || "--",
            },
            {
                accessorKey: "date_of_service",
                header: "Date of Service",
                cell: ({ row }) => {
                    const dateStr = row.original.date_of_service;
                    if (!dateStr) return "--";
                    const parsedDate = parseDateForFormat(dateStr);
                    return formatDate(parsedDate, true) || "--";
                },
            },
            {
                accessorKey: "appointment_type",
                header: "Appointment Type",
                cell: ({ row }) =>
                    makeStringFirstLetterCapital(
                        row?.original?.appointment_type || ""
                    ).replaceAll("_", " ") || "--",
            },
            {
                accessorKey: "payment_type",
                header: "Type",
                cell: ({ row }) =>
                    PAYMENT_LINK_BILL_TYPE_LABELS[row.original.payment_type] ||
                    "--",
            },
            {
                accessorKey: "amount",
                header: "Amount",
                cell: ({ row }) => (
                    <span className="text-sm font-medium text-text">
                        {showMoneyInAppFormat(row.original.amount)}
                    </span>
                ),
            },
        ],
        [paymentLinkData.client_name]
    );

    return (
        <div className="min-h-screen bg-card-bg p-4 flex items-center justify-center">
            <div className="w-full max-w-7xl">
                <Card classNames="p-8 flex flex-col h-[calc(100vh-32rem)] max-h-[800px]">
                    <div className="flex items-center flex-col mb-8 pt-8">
                        <div className="mb-6">
                            <CreditCardIcon />
                        </div>
                        <h1 className="text-title font-medium mb-4 text-center">
                            PAY OUTSTANDING BILLS
                        </h1>
                        <div className="text-center max-w-3xl">
                            <p className="text-base text-gray leading-relaxed">
                                You are about to make a payment for{" "}
                                <span className="font-semibold text-text">
                                    {paymentLinkData.client_name}
                                </span>{" "}
                                for the following bills. Here is a complete
                                breakdown of the bills:
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-24 space-y-6 mb-6">
                        <div className="space-y-4">
                            <div className="max-h-[400px] overflow-y-auto">
                                <Table
                                    data={paymentLinkData.invoices}
                                    columns={columns}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>

                    <div className=" border-border pt-4 mt-auto flex-shrink-0 space-y-4">
                        <div className="flex justify-between items-center">
                            <p className="text-base font-medium text-text">
                                Total Amount
                            </p>
                            <p className="text-lg font-bold text-text">
                                {showMoneyInAppFormat(calculateTotal)}
                            </p>
                        </div>
                        <Button
                            onClick={handleContinue}
                            variant="primary"
                            className="w-full"
                        >
                            Continue to Payment
                        </Button>
                    </div>
                </Card>
            </div>
        </div>
    );
}
